import { mutationOptions, queryOptions } from "@tanstack/react-query";
import {
  ADD_POINT_FOR_WEB_PAYMENT,
  GET_POINT,
  LST_POINT_PCK,
  PAIDY_CAPTURE,
  SAVE_WEB_PAYMENT_POINT_LOG,
  STRIPE_CHECKOUT_CAPTURE,
  STRIPE_CREATE_CHECKOUT_SESSION,
  UPDATE_WEB_PAYMENT_POINT_LOG_STATUS,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import { EVENT_NAMES, trackEvent } from "~/lib/tracking";
import type {
  AddPointForWebPaymentRequest,
  AddPointForWebPaymentResponse,
  PaidyCaptureRequest,
  PaidyCaptureResponse,
  PointPackageListResponse,
  SaveWebPaymentPointLogRequest,
  SaveWebPaymentPointLogResponse,
  StripeCheckoutCaptureRequest,
  StripeCreateCheckoutSessionRequest,
  StripeCreateCheckoutSessionResponse,
  UpdateWebPaymentPointLogStatusRequest,
  UserPointResponse,
} from "./types";

export const userPointQueryOptions = queryOptions({
  queryKey: queryKeys.user.point(),
  queryFn: () => apiClient<UserPointResponse>(GET_POINT),
  staleTime: 60_000,
});

export const pointPackageListQueryOptions = queryOptions({
  queryKey: [...queryKeys.user.all, "pointPackages"] as const,
  queryFn: () => apiClient<PointPackageListResponse>(LST_POINT_PCK),
  staleTime: 5 * 60 * 1000,
});

export const saveWebPaymentPointLogMutationOptions = mutationOptions({
  mutationFn: (req: SaveWebPaymentPointLogRequest) =>
    apiClient<SaveWebPaymentPointLogResponse>(SAVE_WEB_PAYMENT_POINT_LOG, {
      payment_method: req.payment_method,
      amount: req.amount,
      points_granted: req.points_granted,
      status: req.status,
      payment_provider_id: req.payment_provider_id,
    }),
});

export const updateWebPaymentPointLogStatusMutationOptions = mutationOptions({
  mutationFn: (req: UpdateWebPaymentPointLogStatusRequest) =>
    apiClient<void>(UPDATE_WEB_PAYMENT_POINT_LOG_STATUS, {
      payment_provider_id: req.payment_provider_id,
      status: req.status,
    }),
});

export const addPointForWebPaymentMutationOptions = mutationOptions({
  mutationFn: (req: AddPointForWebPaymentRequest) =>
    apiClient<AddPointForWebPaymentResponse>(ADD_POINT_FOR_WEB_PAYMENT, {
      package_id: req.package_id,
      point_type: req.point_type,
    }),
});

export const capturePaidyPaymentMutationOptions = mutationOptions({
  mutationFn: (req: PaidyCaptureRequest) =>
    apiClient<PaidyCaptureResponse>(PAIDY_CAPTURE, {
      payment_id: req.payment_id,
      package_id: req.package_id,
    }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.user.point() });
    trackEvent(EVENT_NAMES.PURCHASE);
  },
});

export const stripeCreateCheckoutSessionMutationOptions = mutationOptions({
  mutationFn: (req: StripeCreateCheckoutSessionRequest) =>
    apiClient<StripeCreateCheckoutSessionResponse>(
      STRIPE_CREATE_CHECKOUT_SESSION,
      {
        package_id: req.package_id,
      },
    ),
});

export const stripeCheckoutCaptureMutationOptions = mutationOptions({
  mutationFn: (req: StripeCheckoutCaptureRequest) =>
    apiClient<void>(STRIPE_CHECKOUT_CAPTURE, {
      checkout_session_id: req.checkout_session_id,
      point_type: req.point_type,
    }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.user.point() });
    trackEvent(EVENT_NAMES.PURCHASE);
  },
});
